import { assetsMap, listPages, readMeta, ROOT_ID, Scene, type Asset, type Matrix, type NodeRecord } from '@flobi/artwork-core';
import * as Y from 'yjs';
import { pageRoomName, rootRoomName, storedState } from '../live/collab';
import { tileUrl } from './content/tileUrl';

/*
 * A whole file as what Open with sends (kit receive/whole.js `contents`; owner, 2 Nov 2026). The media inside it, never a
 * render or an export: every picture and clip its layers show, by media asset, each once in the first place it is found.
 *
 * Artboards are what divide a file, so with several each one is a group, and what sits on no artboard goes ungrouped; with
 * one there is nothing to divide and it goes flat. A page is only storage (artwork-core model/document.ts: nobody sees one),
 * so it never becomes a group. A text layer's words go as text, in reading order; a text used as a mask is a shape, not words.
 * A hidden layer is still in the file and comes along; a hidden paint was switched off, so it doesn't.
 *
 * A picture with no media asset (still uploading) goes with no id, so the kit counts it rather than it vanishing.
 */

type Loose = Record<string, any>;

interface Held { y: number; x: number; assets: string[]; text: string }

export interface WholeFileRow { mediaAssetId?: string; driveFileId?: string; url?: string; mime: string; name: string; thumbnailUrl?: string; width?: number; height?: number; groupId?: string }
export interface WholeFile {
  name: string;
  from: { app: 'artwork'; id: string; name: string };
  groups: { id: string; name: string }[];
  files: WholeFileRow[];
  texts: { text: string; groupId?: string }[];
}

const HTTPS = /^https:\/\//i;
const STORED_MIME = { video: 'video/*', image: 'image/*' } as const;

/** The assets a layer's own paints show. An image layer's picture is its `assetId`; any layer can be filled or stroked with one. */
function assetsOf(node: Loose): string[] {
  const paints = [...(node.fills ?? []), ...(node.strokes ?? [])].filter((paint: Loose) => paint?.type === 'image' && paint.visible !== false && typeof paint.assetId === 'string');
  return [...(node.type === 'image' && typeof node.assetId === 'string' ? [node.assetId] : []), ...paints.map((paint: Loose) => paint.assetId as string)];
}

/** A layer's plain words, or "". */
function wordsOf(node: Loose): string {
  if (node.type !== 'text' || node.isMask) return '';
  const text = (node.runs ?? []).map((run: Loose) => String(run?.text ?? '')).join('');
  return text.trim() ? text : '';
}

/** Everything under `parent`, however it is nested, each with where it stands on the page. */
function heldUnder(scene: Scene, parent: string, out: Held[] = []): Held[] {
  for (const id of scene.childrenOf(parent)) {
    const node = scene.get(id) as NodeRecord | undefined;
    if (!node) continue;
    const at: Matrix = scene.worldTransform(id);
    out.push({ y: at[5], x: at[4], assets: assetsOf(node), text: wordsOf(node) });
    heldUnder(scene, id, out);
  }
  return out;
}

const readingOrder = (a: Held, b: Held) => a.y - b.y || a.x - b.x;

/** One asset as the kit's file, in its group. */
function assetFile(asset: Asset, groupId: string | undefined): WholeFileRow {
  const url = asset.src && HTTPS.test(asset.src) ? asset.src : undefined;
  const video = asset.kind === 'video';
  return {
    ...(asset.mediaAssetId ? { mediaAssetId: asset.mediaAssetId } : {}),
    ...(asset.driveFileId ? { driveFileId: asset.driveFileId } : {}),
    ...(url ? { url } : {}),
    mime: asset.mime || STORED_MIME[video ? 'video' : 'image'],
    name: asset.name || (video ? 'Clip' : 'Picture'),
    ...(url && !video ? { thumbnailUrl: tileUrl(url) } : {}),
    ...(asset.width && asset.height ? { width: asset.width, height: asset.height } : {}),
    ...(groupId ? { groupId } : {}),
  };
}

/** `contents` for a file: what its root says of `assets`, and the scene of each of its pages. */
export function fileContents({ documentId, name, asset, scenes }: { documentId: string; name: string; asset: (id: string) => Asset | undefined; scenes: readonly Scene[] }): WholeFile {
  const boards = scenes.flatMap((scene) => scene.childrenOf(ROOT_ID).filter((id) => scene.get(id)?.type === 'artboard').map((id) => ({ scene, id })));
  const several = boards.length > 1;
  const groups: WholeFile['groups'] = [];
  const files: WholeFileRow[] = [];
  const texts: WholeFile['texts'] = [];
  const seen = new Set<string>();

  const take = (held: readonly Held[], groupId: string | undefined) => {
    for (const { assets, text } of held) {
      for (const id of assets) {
        const found = asset(id);
        // Pictures and clips only: a font is the file's own, and a sketch is code rather than a stored file.
        if ((found?.kind === 'image' || found?.kind === 'video') && !seen.has(id)) (seen.add(id), files.push(assetFile(found, groupId)));
      }
      if (text) texts.push({ text, ...(groupId ? { groupId } : {}) });
    }
  };

  boards.forEach(({ scene, id }, i) => {
    const board = scene.get(id) as Loose;
    const groupId = several ? id : undefined;
    if (several) groups.push({ id, name: String(board.name || '').trim() || `Artboard ${i + 1}` });
    // The artboard's own picture first: it lies behind everything on it.
    take([{ y: 0, x: 0, assets: assetsOf(board), text: '' }, ...heldUnder(scene, id).sort(readingOrder)], groupId);
  });
  for (const scene of scenes) {
    const loose = scene.childrenOf(ROOT_ID).filter((id) => scene.get(id)?.type !== 'artboard');
    take(loose.flatMap((id) => [{ y: 0, x: 0, assets: assetsOf(scene.get(id) as Loose), text: wordsOf(scene.get(id) as Loose) }, ...heldUnder(scene, id)]), undefined);
  }
  return { name, from: { app: 'artwork', id: documentId, name }, groups, files, texts };
}

/** A room's stored state (`artwork.live.state`, the read a file opens from) as a document of its own. */
async function storedDoc(room: string): Promise<Y.Doc> {
  const doc = new Y.Doc();
  const state = (await storedState(room))?.state;
  const bytes = typeof state === 'string' ? Uint8Array.from(atob(state), (c) => c.charCodeAt(0)) : state;
  if (bytes?.byteLength) Y.applyUpdate(doc, bytes);
  return doc;
}

/** What the editor already holds of a file open in it. */
export interface HeldFile { readonly root: Y.Doc; readonly pageId: string; readonly scene: Scene }

/**
 * Reads a whole file. From the Home nothing is open, so it reads what is stored, one room after another; that is at most a
 * few seconds behind someone working in the file. In the editor the open page and the root are read where they are, live.
 * Nothing here writes: no flush, no room joined.
 */
export async function readWholeFile({ documentId, name, open }: { documentId: string; name?: string; open?: HeldFile | null }): Promise<WholeFile> {
  const made: Y.Doc[] = [];
  const stored = async (room: string) => {
    const doc = await storedDoc(room);
    made.push(doc);
    return doc;
  };
  try {
    const root = open?.root ?? (await stored(rootRoomName(documentId)));
    const scenes: Scene[] = [];
    for (const page of listPages(root)) scenes.push(open?.pageId === page.id ? open.scene : new Scene(await stored(pageRoomName(documentId, page.id))));
    const assets = assetsMap(root);
    return fileContents({ documentId, name: name || readMeta(root).name, asset: (id) => assets.get(id), scenes });
  } finally {
    made.forEach((doc) => doc.destroy());
  }
}
